import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import moment, { Moment } from 'moment';
import { useFormik } from 'formik';
import { date, object } from 'yup';
import isEmpty from 'lodash/isEmpty';

import { useTheme } from '@mui/styles';

import LoadingButton from '@mui/lab/LoadingButton';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import messages from './messages';
import { DatePickerField } from 'src/components/DatePickerField';
import { toMoment } from 'src/utils/datetime';
import VerificationDeclineForm from '../VerificationDeclineForm/VerificationDeclineForm';

import { ASSET_TYPE, VerificationType } from 'src/pages/VerificationsPage/api';

import ConfirmationDialog from 'src/components/ConfirmationDialog';

interface Props {
  file?: UCM.VerificationFilesType | null;
  tab: VerificationType;
  handleApprove: (params: Record<string, string>) => void;
  handleReject: (params: Record<string, string>) => void;
  loading: boolean;
}

interface FileState {
  issueDate?: Moment | null;
  expirationDate?: Moment | null;
}

export default function VerificationFileFields({
  file,
  tab,
  handleApprove,
  handleReject,
  loading,
}: Props) {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const [validateOnChange, setValidateOnChange] = useState(false);

  const verificationFile = useMemo(() => {
    return file as UCM.VerificationFilesType;
  }, [file]);

  const initialValues: FileState = useMemo(
    () => ({
      issueDate: verificationFile?.issueDate
        ? toMoment(verificationFile?.issueDate)
        : null,
      expirationDate: verificationFile?.expirationDate
        ? toMoment(verificationFile?.expirationDate)
        : null,
    }),
    [verificationFile],
  );

  const isPicture = useMemo(() => {
    return tab === 'pictures';
  }, [tab]);

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validateOnChange: validateOnChange,
    validationSchema: object().shape({
      issueDate: isPicture ? date() : date().required(),
      expirationDate: isPicture ? date() : date().required(),
    }),
    onSubmit: () => {},
  });

  const { values, setFieldValue, errors, resetForm } = formik;

  useEffect(() => {
    if (file?.uuid && validateOnChange) {
      resetForm();
      setValidateOnChange(false);
    }
  }, [file?.uuid]);

  const handleChangeDate = useCallback(
    (date: Moment | Moment[] | null, name: keyof FileState) => {
      setFieldValue(name, date);
      return Promise.resolve();
    },
    [],
  );

  const assetType = useMemo(() => {
    switch (tab) {
      case 'matriculation_files':
        return ASSET_TYPE.MATRICULATION_FILE;
      case 'work_permissions':
        return ASSET_TYPE.WORK_PERMISSION;
      case 'certificate_files':
        return ASSET_TYPE.CERTIFICATE_FILE;
      case 'pictures':
        return ASSET_TYPE.PICTURE;
      default:
        return '';
    }
  }, [tab]);

  const handlePressApprove = useCallback(async () => {
    let params = {};
    if (!isPicture) {
      params = {
        issue_date: values.issueDate?.format(moment.HTML5_FMT.DATE),
        expiration_date: values.expirationDate?.format(moment.HTML5_FMT.DATE),
      };
    }
    handleApprove(params);
  }, [values.issueDate, values.expirationDate, handleApprove]);

  const handleDecline = useCallback(
    (reasonCode: string) => {
      handleReject({ rejection_reason: reasonCode });
    },
    [handleReject],
  );

  const handleCheckFormik = useCallback(
    async (callback: () => void) => {
      let isValid = true;
      if (tab.match(/matriculation_files|work_permissions|certificate_files/)) {
        setValidateOnChange(true);
        const errors = await formik.validateForm();
        isValid = isEmpty(errors);
      }

      if (!isValid) return false;

      callback();
    },
    [tab],
  );

  const disabledButtons = useMemo(() => {
    return !verificationFile || loading;
  }, [verificationFile, loading]);

  return (
    <Stack
      borderRadius={1.5}
      bgcolor={theme.palette.common.white}
      p={1.5}
      direction="column"
      spacing={1}
    >
      <Typography variant="inherit" fontSize={12}>
        {verificationFile?.fileName}
      </Typography>
      <Typography
        variant="inherit"
        fontSize={12}
        color={theme.palette.grey[700]}
      >
        {formatMessage(messages.uploadedOnLabel, {
          date: verificationFile?.createdAt ?? '-',
        })}
      </Typography>
      {!isPicture && (
        <Grid container spacing={2}>
          <Grid size={6}>
            <DatePickerField
              allowPicker
              allowTyping
              name="issueDate"
              onChange={(date) => handleChangeDate(date, 'issueDate')}
              onBlurChange={(date) => handleChangeDate(date, 'issueDate')}
              label={formatMessage(messages.startDateLabel)}
              error={!!errors.issueDate}
              helperText={errors.issueDate}
              value={values.issueDate}
            />
          </Grid>
          <Grid size={6}>
            <DatePickerField
              allowPicker
              allowTyping
              name="expirationDate"
              onChange={(date) => handleChangeDate(date, 'expirationDate')}
              onBlurChange={(date) => handleChangeDate(date, 'expirationDate')}
              label={formatMessage(messages.expirationDateLabel)}
              error={!!errors.expirationDate}
              helperText={errors.expirationDate}
              value={values.expirationDate}
            />
          </Grid>
        </Grid>
      )}
      <Grid container spacing={1} justifyContent="flex-end" mt={1}>
        <VerificationDeclineForm
          assetType={assetType}
          onConfirm={handleDecline}
          disabled={disabledButtons}
          loading={loading}
        />
        <ConfirmationDialog
          onConfirm={handlePressApprove}
          message={formatMessage(messages.approveConfirmMessage)}
        >
          {(handleOpen) => (
            <LoadingButton
              variant="contained"
              color="primary"
              disabled={disabledButtons}
              onClick={(event) => handleCheckFormik(() => handleOpen(event))}
              loading={loading}
            >
              {formatMessage(messages.approveButton)}
            </LoadingButton>
          )}
        </ConfirmationDialog>
      </Grid>
    </Stack>
  );
}
